import { useState } from "react";
import { Link } from "wouter";
import { Button, Card, Badge } from "@/components/ui";
import {
  BookOpen,
  Brain,
  Zap,
  ArrowRight,
  Sun,
  Moon,
  Check,
  Crown,
  Star,
  ChevronDown,
  FileText,
  MessageSquare,
  Layers,
  Clock,
  Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/lib/theme-context";
import { Footer } from "@/components/layout/footer";

function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const faqItems = [
  {
    q: "What does Lerni do?",
    a: "Lerni turns your study materials into summaries, MCQs, and flashcards — instantly. Upload your notes or PDFs and Lerni helps you study faster and understand more.",
  },
  {
    q: "What file types are supported?",
    a: "Lerni supports PDFs, notes, and other text-based study materials. If you can study from it, Lerni can work with it.",
  },
  {
    q: "What is Lerni Bot?",
    a: "Lerni Bot is your personal AI study assistant. Ask it to explain topics, answer questions about your material, or guide you through revision — it's like having a tutor on demand.",
  },
  {
    q: "What do paid plans include?",
    a: "Paid plans give you unlimited files, unlimited summaries, unlimited MCQs, unlimited flashcards, Lerni Bot access, priority generation, and email support. Everything you need to study without limits.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes, absolutely. Cancel anytime with no hidden fees and no questions asked. You're never locked in.",
  },
  {
    q: "Is my data safe?",
    a: "Your files are yours. We process everything securely, never share your data with third parties, and never use it for anything other than generating your study material.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-foreground hover:bg-muted/50 transition-colors"
      >
        <span>{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <div className="px-6 pb-5 text-muted-foreground leading-relaxed border-t border-border pt-4">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const pricingPlans = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Perfect to try things out",
    icon: Zap,
    popular: false,
    features: [
      "3 files per month",
      "Full summaries",
      "Full MCQs",
      "Full flashcards",
      "15 Lerni Bot messages per month",
    ],
    cta: "Get Started Free",
    href: "/register",
  },
  {
    name: "Weekly",
    price: "$1.99",
    period: "/week",
    description: "Great for exam crunch time",
    icon: Clock,
    popular: false,
    features: [
      "Unlimited files",
      "Unlimited summaries",
      "Unlimited MCQs",
      "Unlimited flashcards",
      "Lerni Bot included",
      "Faster generation",
      "Email support",
    ],
    cta: "Start Weekly",
    href: "/register",
  },
  {
    name: "Monthly",
    price: "$4.99",
    period: "/month",
    description: "Best for consistent study",
    icon: Crown,
    popular: true,
    features: [
      "Unlimited files",
      "Unlimited summaries",
      "Unlimited MCQs",
      "Unlimited flashcards",
      "Lerni Bot included",
      "Priority generation",
      "Email support",
    ],
    cta: "Start Monthly",
    href: "/register",
  },
  {
    name: "Yearly",
    price: "$49",
    period: "/year",
    description: "Best value for serious students",
    icon: Star,
    popular: false,
    features: [
      "Unlimited files",
      "Unlimited summaries",
      "Unlimited MCQs",
      "Unlimited flashcards",
      "Lerni Bot included",
      "Priority generation",
      "Email support",
      "Early access to new features",
    ],
    cta: "Go Yearly",
    href: "/register",
  },
];

export default function Landing() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background overflow-hidden selection:bg-primary/20">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 glass-panel border-x-0 border-t-0 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-2xl tracking-tight text-foreground">
              Lerni
            </span>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7">
            {[
              { label: "Features", id: "features" },
              { label: "Pricing", id: "pricing" },
              { label: "FAQ", id: "faq" },
            ].map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link
              href="/login"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors hidden sm:block"
            >
              Sign In
            </Link>
            <Link href="/register">
              <Button
                size="sm"
                className="rounded-full shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-4 sm:px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-primary/10 to-accent/10 blur-[100px] rounded-full -z-10 pointer-events-none" />

        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-primary/10 shadow-sm mb-8 text-sm font-semibold text-primary backdrop-blur-sm"
          >
            <Zap className="w-4 h-4" />
            <span>AI-Powered Learning Platform</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold text-foreground max-w-4xl mx-auto leading-[1.1]"
          >
            Transform any material into{" "}
            <span className="text-gradient">active knowledge.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            Upload PDFs, notes, or text. Let Lerni instantly generate summaries,
            flashcards, quizzes, and let you chat with your material.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link href="/register">
              <Button size="lg" className="w-full sm:w-auto group rounded-2xl text-lg h-14">
                Start Studying Free
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link href="/login">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto rounded-2xl text-lg h-14 bg-card/50 backdrop-blur-sm"
              >
                I already have an account
              </Button>
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20 max-w-5xl mx-auto relative rounded-3xl overflow-hidden shadow-2xl border border-border/30"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent z-10" />
          <img
            src={`${import.meta.env.BASE_URL}images/hero-bg.png`}
            alt="Lerni AI study platform"
            className="w-full h-auto object-cover aspect-video bg-muted"
          />
        </motion.div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-24 bg-card relative scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold">
              Everything you need to ace it.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Stop reading passively. Start learning actively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: FileText,
                title: "Structured Summaries",
                description:
                  "Turn lectures and dense PDFs into clear, structured summaries you can actually study from.",
              },
              {
                icon: Layers,
                title: "Key Points",
                description:
                  "Extract the most important concepts instantly — no more re-reading pages to find what matters.",
              },
              {
                icon: Brain,
                title: "Flashcards & MCQs",
                description:
                  "Auto-generate flashcards for revision and multiple-choice questions to test your understanding.",
              },
              {
                icon: MessageSquare,
                title: "Lerni Bot",
                description:
                  "Chat directly with your material. Ask Lerni Bot anything about what you uploaded and get instant answers.",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-background rounded-3xl p-8 border border-border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-background relative scroll-mt-20">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-to-br from-primary/5 to-accent/5 blur-[100px] rounded-full -z-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="outline" className="mb-4 bg-card/50 backdrop-blur-sm">
                <Star className="w-3 h-3 mr-1" /> Simple Pricing
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold">
                Start free. Upgrade when ready.
              </h2>
              <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
                Choose the plan that matches your study load. No hidden fees, cancel anytime.
              </p>
            </motion.div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card
                  className={`relative p-6 h-full flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${plan.popular ? "border-primary shadow-lg shadow-primary/10 ring-1 ring-primary/20" : ""}`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground shadow-lg px-4 whitespace-nowrap">
                      Most Popular
                    </Badge>
                  )}
                  <div className="mb-5">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${plan.popular ? "bg-primary/10" : "bg-muted"}`}>
                      <plan.icon className={`w-6 h-6 ${plan.popular ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <h3 className="text-xl font-display font-bold">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                  </div>
                  <div className="mb-5">
                    <span className="text-4xl font-bold font-display">{plan.price}</span>
                    <span className="text-muted-foreground ml-1">{plan.period}</span>
                  </div>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${plan.popular ? "text-primary" : "text-muted-foreground"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link href={plan.href}>
                    <Button
                      className={`w-full rounded-xl h-12 transition-colors ${plan.popular ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                      variant={plan.popular ? "default" : "outline"}
                      size="lg"
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Cancel anytime. No hidden fees. Secure checkout.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-card relative scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <Badge variant="outline" className="mb-4 bg-card/50 backdrop-blur-sm">
              FAQ
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold">
              Common questions
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Everything you need to know about Lerni.
            </p>
          </div>

          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>

          <div className="mt-14 text-center">
            <p className="text-muted-foreground mb-4">Still have questions?</p>
            <Link href="/register">
              <Button variant="outline" size="lg" className="rounded-full">
                Get started free — no credit card required
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
